import React, { useCallback } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { segmentTrack } from 'flow/actions/segment-actions';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { ChannelValues } from 'flow/components/tickets/messageChannels';

interface UseTextChangeTrackingParams {
    sentSegmentTracks: Set<string>;
    setSentSegmentTracks: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export const useTextChangeTracking = ({
    sentSegmentTracks,
    setSentSegmentTracks,
}: UseTextChangeTrackingParams) => {
    const dispatch = useAppDispatch();

    const trackTextChangeStart = useCallback(
        (channelSelected: 'internal' | ChannelValues) => {
            let trackingKey: string;
            let segmentEvent: string;
            let segmentProps: Record<string, string> | undefined;

            if (channelSelected === 'internal') {
                trackingKey = 'internal';
                segmentEvent = TICKET_STRINGS.USER_BEGAN_WRITING_INTERNAL_NOTE;
            } else if (channelSelected === 'whatsapp') {
                trackingKey = 'whatsapp';
                segmentEvent = TICKET_STRINGS.USER_BEGAN_WRITING_WHATSAPP_REPLY;
                segmentProps = { component: 'Ticket input field' };
            } else if (channelSelected === 'email') {
                trackingKey = 'email';
                segmentEvent = TICKET_STRINGS.USER_BEGAN_WRITING_EMAIL_REPLY;
                segmentProps = { component: 'Ticket input field' };
            } else if (channelSelected === 'sms') {
                trackingKey = 'sms';
                segmentEvent =
                    TICKET_STRINGS.USER_BEGAN_WRITING_TEXT_MESSAGE_REPLY;
            } else {
                trackingKey = channelSelected; // Fallback to using the channel as the key for any other channels
                segmentEvent = `[Tickets] User began writing a ${channelSelected} message reply`;
                segmentProps = { component: 'Ticket input field' };
            }

            // Only track if we haven't sent this event yet
            if (!sentSegmentTracks.has(trackingKey)) {
                dispatch(segmentTrack(segmentEvent, segmentProps));
                setSentSegmentTracks((prev) => new Set(prev).add(trackingKey));
            }
        },
        [dispatch, sentSegmentTracks, setSentSegmentTracks],
    );

    return { trackTextChangeStart };
};
